import React, { useEffect, useRef, useState } from "react";

/**
 * Counts up from 0 to `value` when scrolled into view.
 * value can be a number or string like "1,000,000+" — we detect the numeric part
 * and preserve suffix.
 */
const parseTarget = (value) => {
  if (typeof value === "number") return { num: value, suffix: "", prefix: "" };
  const str = String(value);
  const m = str.match(/^([^\d]*)([\d,]+)(.*)$/);
  if (!m) return { num: 0, suffix: str, prefix: "" };
  const num = parseInt(m[2].replace(/,/g, ""), 10);
  return { num, prefix: m[1] || "", suffix: m[3] || "" };
};

const formatNumber = (n) => {
  if (n >= 1_000_000) {
    return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "M";
  }
  if (n >= 1_000) {
    return n.toLocaleString();
  }
  return String(n);
};

const AnimatedCounter = ({
  target,
  duration = 2000,
  className = "",
  testId,
}) => {
  const { num, prefix, suffix } = parseTarget(target);
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - t, 3);
              setDisplay(Math.floor(num * eased));
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(num);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [num, duration]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {formatNumber(display)}
      {suffix}
    </span>
  );
};

export default AnimatedCounter;
